import Injectorator from './Injectorator.js';

function Controllerator() {
    this.controllers = {};
    this.injector = new Injectorator();
    this._evaluatedControllers = [];
    this._eagerServices = [];
    this._pendingRun = false;
    this._running = false;

    this.registerInstance('$controllerator', this);

    this._defaultDependencies = ['$element', '$controllerator'];
}

Controllerator.prototype = {
    _makeErrorAndLog(msg) {
        const errorMessage = `[C] ${msg}`;
        console.error(errorMessage);
        return new Error(errorMessage);
    },

    _getProperty(subject, name) {
        const parts = name.split('.');
        return parts.reduce((prev, cur) => (prev ? prev[cur] : null), subject);
    },

    _evaluateElement(ele) {
        const attr = ele.getAttribute('data-controller').trim();

        if (attr === '') {
            console.warn(
                '[C] A data-controller attribute cannot have an empty value'
            );
            return;
        }

        const controllerNames = attr
            .split(/\s+/)
            .filter(name => name !== '__proto__');

        controllerNames.forEach(
            this._evaluateElementController.bind(this, ele)
        );
    },

    _loadController(name) {
        return this._parseController(name, this._getProperty(window, name));
    },

    _parseController(name, controller) {
        if (!controller) {
            return null;
        } else if (controller.callable) {
            if (!controller.name) {
                controller.name = name;
            }
            return controller;
        } else {
            return {
                name,
                callable: controller,
            };
        }
    },

    _getElementRecord(ele) {
        return (
            this._evaluatedControllers.filter(
                eleRecord => eleRecord.ele === ele
            )[0] || null
        );
    },

    _isEvaluated(ele, controllerName) {
        const eleRecord = this._getElementRecord(ele);
        if (!eleRecord) {
            return false;
        }
        return eleRecord.controllers.indexOf(controllerName) !== -1;
    },

    _markAsEvaluated(ele, controllerName) {
        const eleRecord = this._getElementRecord(ele);
        if (!eleRecord) {
            this._evaluatedControllers.push({
                ele,
                controllers: [controllerName],
            });
            return;
        }
        eleRecord.controllers.push(controllerName);
    },

    _evaluateElementController(ele, controllerName) {
        const controller = this.controllers.hasOwnProperty(controllerName)
            ? this.controllers[controllerName]
            : this._loadController(controllerName);
        let msg;
        if (controller) {
            try {
                if (
                    controller.dependencies &&
                    controller.callable.length !==
                        controller.dependencies.length
                ) {
                    msg = `Warning: Arity mismatch in '${controller.name}'. Controller takes ${controller
                        .callable
                        .length} arguments, dependency provides ${controller
                        .dependencies.length}`;
                    this._makeErrorAndLog(msg);
                }

                if (this._isEvaluated(ele, controllerName)) {
                    return;
                }

                this.injector.call(
                    controller.callable,
                    controller.dependencies || this._defaultDependencies,
                    { $element: ele }
                );

                this._markAsEvaluated(ele, controllerName);
            } catch (e) {
                msg = `Error initializing controller: ${controllerName}, caused by: ${e.message}`;
                this._makeErrorAndLog(msg);
                e.message = msg;
                console.error(e.stack);
                if (window.onerror) {
                    window.onerror(e);
                }
            }
        } else {
            throw this._makeErrorAndLog(
                `Could not find controller: ${controllerName}`
            );
        }
    },

    runController(controllerName, ele) {
        this._evaluateElementController(ele, controllerName);
    },

    registerGlobalInstances(names) {
        if (names) {
            names.forEach(function(name) {
                if (window[name]) {
                    this.registerInstance(name, window[name]);
                } else {
                    throw this._makeErrorAndLog(
                        `Could not find instance: ${name}`
                    );
                }
            });
        }
    },

    registerInstance(name, instance) {
        this.injector.registerInstance(name, instance);
    },

    registerService(definition) {
        if (definition) {
            this.injector.register(
                definition.name,
                definition.callable,
                definition.dependencies || []
            );
            if (definition.eager) {
                this._eagerServices.push(definition.name);
            }
        } else {
            this._makeErrorAndLog(
                `Non-extant service attempted registered: ${definition}`
            );
        }
    },

    registerController(name, definition) {
        const subject = this._parseController(name, definition);
        if (subject) {
            // Hi there! This test is naive!
            this.controllers[name] = definition;
        }
    },

    register(name, subject) {
        this.injector.register(name, subject, subject.dependencies || []);
    },

    run() {
        let maxLoops = 10;
        this._pendingRun = true;
        this._eagerServices.forEach(this.injector.get.bind(this.injector));
        this._eagerServices = [];

        // The _running/_pendingRun machinery is to make any recursive call to controller.run()
        // cause the controller to re-run when finished with its current evaluation, not perform it
        // recursivley. Recursive running doesn't hurt, but if the invoking call happened in a
        // promise, or somewhere else that does exception swallowing, there were no way to see
        // that errors occured during the recursive controllerator run.
        if (!this._running) {
            while (this._pendingRun) {
                if (maxLoops-- < 0) {
                    throw this._makeErrorAndLog(
                        'Max controllerator.run() loops hit! Possible recursion error'
                    );
                }

                this._pendingRun = false;
                this._running = true;

                const eles = Array.from(
                    document.querySelectorAll('[data-controller]')
                );
                eles.forEach(this._evaluateElement.bind(this));
                this._running = false;

                // Cleanup: remove elements that have been removed from the document
                this._evaluatedControllers = this._evaluatedControllers.filter(
                    eleRecord => document.contains(eleRecord.ele)
                );
            }
        }
    },
};

export default Controllerator;



// WEBPACK FOOTER //
// ./lib/Controllerator.js